import React from "react";
import { Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { isArray, toUpper } from "lodash";
import openSansRegular from '../fonts/OpenSans/OpenSans-Regular.ttf'
import openSansItalic from '../fonts/OpenSans/OpenSans-Italic.ttf'
import openSansBold from '../fonts/OpenSans/OpenSans-Bold.ttf'
import openSansBoldItalic from '../fonts/OpenSans/OpenSans-BoldItalic.ttf'
import { formatEventDateLong } from '../helpers/dateHelper';
import { isValid } from '../helpers/validationHelper';

Font.register({ family: 'OpenSans', fonts: [
  { src: openSansRegular },
  { src: openSansItalic, fontStyle: 'italic' },
  { src: openSansBold, fontWeight: 700 },
  { src: openSansBoldItalic, fontStyle: 'italic', fontWeight: 700 },
 ]});

const EventListPDF = (props) => {
  const { events = [], account_settings = {}, type = "detailed" } = props

  const styles = StyleSheet.create({
    page: {
      paddingTop: 35,
      paddingBottom: 65,
      paddingHorizontal: 35
    },
    row: {
      flexDirection: 'row',
      paddingVertical: 8
    },
    col: {
      flex: 1
    },
    section: {
      borderBottom: "2px solid #CCC"
    },
    header: {
      marginBottom: 20
    },
    headerText: {
      fontSize: 12,
      fontFamily: "OpenSans",
      fontWeight: 700,
      textAlign: "center"
    },
    subTitleText: {
      fontSize: 12,
      fontFamily: "OpenSans",
      fontWeight: 700,
      textAlign: "center",
      marginTop: 10
    },
    dateText: {
      fontSize: 10,
      fontFamily: "OpenSans",
      textAlign: "center"
    },
    emptyText: {
      fontSize: 8,
      fontFamily: "OpenSans",
      color: "#777777",
      backgroundColor: "#F7F7F7",
      paddingVertical: 10,
      paddingHorizontal: 5,
      textAlign: 'center'
    },
    labelText: {
      fontSize: 8,
      fontFamily: "OpenSans",
      fontWeight: 700,
    },
    dataText: {
      fontSize: 8,
      fontFamily: "OpenSans",
    },
    mutedText: {
      fontSize: 8,
      fontFamily: "OpenSans",
      color: "#777777"
    },
    stageText: {
      fontSize: 7,
      fontFamily: "OpenSans",
      fontWeight: 700,
      textAlign: 'right'
    },
    rowHeader: {
      display: "flex",
      flexDirection: 'row',
      borderBottom: "1px solid #e8e8e8",
      backgroundColor: "#F7F7F7",
      padding: 5
    },
    rowHeaderText: {
      fontSize: 7,
      fontFamily: "OpenSans",
      fontWeight: 700
    },
  });

  const getVenueDetails = (event) => {
    var venueName = ""
    if (isArray(event.venues) && event.venues.length > 0) {
      const venue = event.venues[0]
      if (venue.city) {
        const cityState = venue.state ? `${venue.city}, ${venue.state}` : venue.city
        venueName = `${venue.venue_name} (${cityState})`
      } else {
        venueName = venue.venue_name
      }
    }
    return venueName
  }

  const getStageName = (event) => {
    return isValid(event.stage_name) ? toUpper(event.stage_name) : "UNKNOWN"
  }

  const renderDetailedEvent = (event, index) => {
    const clients = isArray(event.clients) ? event.clients : []
    const employees = isArray(event.employees) ? event.employees : []
    return (
      <View style={{ borderTop: "2px solid #CCC" }} key={index}>
        <View style={[styles.row, { borderBottom: "1px solid #e0e0e0" }]}>
          <View style={styles.col}>
            <Text style={styles.labelText}>{event.event_name}</Text>
            <Text style={styles.dataText}>{formatEventDateLong(event.event_date, account_settings)}</Text>
            <Text style={styles.dataText}>{event.account_event_type_name}</Text>
            <Text style={styles.dataText}>{getVenueDetails(event)}</Text>
          </View>
          <View style={{ width: 200 }}>
            <Text style={styles.stageText}>{getStageName(event)}</Text>
          </View>
        </View>
        <View style={[styles.row, { paddingHorizontal: 5 }]}>
          <View style={styles.col}>
            <Text style={[styles.mutedText, { fontSize: 6 }]}>CLIENTS</Text>
            { clients.map((x,i) => (
              <Text style={styles.dataText}>{x.first_name} {x.last_name} <Text style={styles.mutedText}>({x.role_name})</Text></Text>
            ))}
            { clients.length == 0 && <Text style={styles.dataText}>--</Text> }
          </View>
          <View style={styles.col}>
            <Text style={[styles.mutedText, { fontSize: 6 }]}>ASSIGNED STAFF</Text>
            { employees.map((x,i) => (
              <Text style={styles.dataText}>{x.first_name} {x.last_name} <Text style={styles.mutedText}>({x.role_name})</Text></Text>
            ))}
            { employees.length == 0 && <Text style={styles.dataText}>--</Text> }
          </View>
        </View>
      </View>
    )
  }

  const renderSummaryEvent = (event, index) => {
    return (
      <View style={[styles.row, { borderBottom: "1px solid #e0e0e0" }]} key={index}>
        <View style={{ width: 200 }}>
          <Text style={styles.labelText}>{event.event_name}</Text>
          <Text style={styles.dataText}>{formatEventDateLong(event.event_date, account_settings)}</Text>
        </View>
        <View style={{ width: 200 }}>
          <Text style={styles.dataText}>{event.account_event_type_name}</Text>
          <Text style={styles.dataText}>{getVenueDetails(event)}</Text>
        </View>
        <View style={styles.col}>
          <Text style={styles.stageText}>{getStageName(event)}</Text>
        </View>
      </View>
    )
  }
  
  const renderData = () => {
    if (events.length == 0) {
      return (
        <Text style={styles.emptyText}>No events to display</Text>
      )
    }
    if (type == "summary") {
      return (
        <View>
          <View style={styles.rowHeader}>
            <View style={{ width: 200 }}>
              <Text style={styles.rowHeaderText}>EVENT NAME / DATE</Text>
            </View>
            <View style={{ width: 200 }}>
              <Text style={styles.rowHeaderText}>EVENT TYPE / VENUE</Text>
            </View>
            <View style={styles.col}>
              <Text style={[styles.rowHeaderText, { textAlign: 'right' }]}>STAGE</Text>
            </View>
          </View>
        { events.map((e,i) => renderSummaryEvent(e,i))}
      </View>
      )
    }
    return (
      <View style={{ borderBottom: "2px solid #CCC" }}>
        { events.map((e,i) => renderDetailedEvent(e,i))}
      </View>
    )
  }

  const renderDocument = () => {
    return (
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerText}>Events</Text>
          <Text style={styles.dateText}>{ type == "summary" ? "Summary View" : "Detailed View" }</Text>
        </View>
        { renderData() }
      </Page>
    )
  }

  return renderDocument()
}

export default EventListPDF;
